import { defaultBusiness as NAP } from "@/lib/site-config";
import { createFileRoute, Link } from "@tanstack/react-router";
import { HelpfulPricingGuides } from "@/components/HelpfulPricingGuides";
import { Check, Phone, MessageCircle } from "lucide-react";
import { getBusinessConfig } from "@/lib/site-config";
import { seoHead, SITE_ORIGIN, localBusinessSchema, complianceToBusinessOpts } from "@/lib/seo";
import { getPublicConfig } from "@/lib/site-content.functions";
import { FAQAccordion, faqSchema } from "@/components/FAQAccordion";
import { PrimaryCTA } from "@/components/PrimaryCTA";

const PRICING_FAQS = [
  {
    q: "How much is an MOT in East London?",
    a: "MOTs at Woodseer Autos are typically £30–£40 for most cars — call with your reg and we'll confirm availability and the exact class. Class 5 and Class 7 vans are slightly more. We never charge a 'booking fee' on top.",
  },
  {
    q: "What does a service cost?",
    a: "Indicative starting prices: interim from around £89, full service from around £149. Final price depends on the vehicle, parts and what we find on inspection — we always quote in writing before any work starts.",
  },
  {
    q: "Do you charge for diagnostics?",
    a: "We charge a competitive flat fee for a proper diagnostic scan and live-data check — call for the current price. If you go ahead with the repair we recommend, the diagnostic charge typically comes off the final bill.",
  },
  {
    q: "Are your prices fixed or negotiable?",
    a: "Our quotes are fixed before work begins. We don't add surprise charges. If something extra needs doing mid-job we stop, call you and only continue with your approval.",
  },
  {
    q: "Do you price-match other East London garages?",
    a: "We focus on honest pricing rather than headline promotions. If you've had a like-for-like written quote elsewhere, send it over — we'll usually be in the same range using better-quality OE parts.",
  },
  {
    q: "Is VAT included?",
    a: "MOT prices are VAT-exempt and shown gross. Service, repair and bodywork prices we quote are typically ex-VAT — your written quote and invoice will show VAT clearly.",
  },
];

const TIERS: { name: string; from: string; bullets: string[]; cta: string; highlight?: boolean }[] = [
  {
    name: "MOT Test",
    from: "£30–£40",
    bullets: ["Class 4 cars (under 3,000kg)", "Class 5 & 7 vans on request", "Free retest within DVSA window", "Honest pass/fail with plain advisories"],
    cta: "Book MOT",
  },
  {
    name: "Interim Service",
    from: "From £89",
    bullets: ["Oil & filter change", "Brake & tyre inspection", "Fluid top-ups", "30-point health check", "Service book stamped"],
    cta: "Get a quote",
    highlight: true,
  },
  {
    name: "Full Service",
    from: "From £149",
    bullets: ["Everything in interim", "Air & cabin filters", "Spark plugs (where due)", "Brake fluid check", "Suspension & exhaust check"],
    cta: "Get a quote",
  },
  {
    name: "Diagnostics",
    from: "Competitive",
    bullets: ["Full OBD-II scan", "Live data check", "Plain-English fault report", "Fee comes off the repair if you proceed"],
    cta: "Talk to us",
  },
];

export const Route = createFileRoute("/pricing")({
  loader: async () => {
    const cfg = await getPublicConfig().catch(() => null);
    return { compliance: cfg?.compliance || null };
  },
  head: ({ loaderData }) =>
    seoHead({
      path: "/pricing",
      title: "Garage Pricing East London | MOT £30–£40 | Woodseer Autos",
      description:
        `Honest, indicative garage pricing in East London. MOTs typically £30–£40, service from £89, diagnostics priced competitively. Written quote before any work — call ${NAP.phone}.`,
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: "Pricing", path: "/pricing" },
      ],
      jsonLd: [
        localBusinessSchema(complianceToBusinessOpts(loaderData?.compliance)),
        faqSchema(PRICING_FAQS) as Record<string, unknown>,
        {
          "@context": "https://schema.org",
          "@type": "PriceSpecification",
          priceCurrency: "GBP",
          minPrice: 30,
          maxPrice: 350,
          eligibleQuantity: { "@type": "QuantitativeValue", unitText: "per service" },
          url: `${SITE_ORIGIN}/pricing`,
        } as Record<string, unknown>,
      ],
    }),
  component: PricingPage,
});

function PricingPage() {
  const biz = getBusinessConfig();
  return (
    <>
      <section className="py-14 md:py-20 bg-secondary">
        <div className="mx-auto max-w-5xl px-4 text-center">
          <p className="text-[11px] font-bold tracking-[0.22em] uppercase text-primary">Honest Pricing</p>
          <h1 className="mt-3 text-4xl md:text-5xl font-extrabold text-foreground tracking-tight">East London garage pricing — no surprises</h1>
          <p className="mt-4 text-base text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            Indicative starting prices for the most-requested jobs. Every quote is written and confirmed before any work begins. No booking fees, no hidden extras — and no work carried out without your approval.
          </p>
          <p className="mt-3 text-xs text-muted-foreground max-w-xl mx-auto">
            MOTs typically £30–£40 — call with your reg to confirm availability and vehicle class.
          </p>
        </div>
      </section>

      <section className="py-12 md:py-16">
        <div className="mx-auto max-w-6xl px-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {TIERS.map((t) => (
            <div key={t.name} className={`rounded-2xl border p-6 ${t.highlight ? "border-primary/50 bg-primary/[0.04]" : "border-border bg-card"}`}>
              <h2 className="text-sm font-bold uppercase tracking-wider text-foreground">{t.name}</h2>
              <p className="mt-2 text-3xl font-extrabold text-foreground">{t.from}</p>
              <ul className="mt-5 space-y-2 text-sm text-muted-foreground">
                {t.bullets.map((b) => (
                  <li key={b} className="flex items-start gap-2"><Check className="h-4 w-4 text-primary shrink-0 mt-0.5" />{b}</li>
                ))}
              </ul>
              <Link to="/book" className="mt-6 inline-flex w-full items-center justify-center rounded-lg bg-primary px-4 py-2.5 text-sm font-bold text-primary-foreground hover:bg-primary/90 transition-colors">
                {t.cta} →
              </Link>
            </div>
          ))}
        </div>

        <div className="mx-auto max-w-3xl px-4 mt-10 text-center">
          <p className="text-sm text-muted-foreground">Want a quick written quote for your exact vehicle? Call <a href={biz.phoneHref} className="text-primary font-bold hover:underline">{biz.phone}</a> with your reg, or WhatsApp us a photo of the work needed.</p>
          <div className="mt-5 flex justify-center">
            <PrimaryCTA location="pricing-quote-strip" intent="written quote" variant="light" />
          </div>
        </div>
      </section>

      <HelpfulPricingGuides />
      <FAQAccordion faqs={PRICING_FAQS} title="Pricing FAQs" />
    </>
  );
}
